import React, { useState, useEffect } from "react";
import {
  HiOutlineCalendar,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineExclamationTriangle,
  HiOutlineChartBar,
  HiOutlineCog,
} from "react-icons/hi2";
import { authService } from "../../../services";
import masterDataService from "../../../services/masterDataService";
import pmMatrixService from "../../../services/pmMatrixService";
import PageLoading from "../../../components/common/PageLoading";
import "./PlannedMaintenanceDashboard.css";

const CACHE_KEY = "pm_dashboard_data";
const CACHE_TIMESTAMP_KEY = "pm_dashboard_timestamp";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Synchronous cache loading function
const getCachedPMDashboardData = () => {
  try {
    const cachedData = sessionStorage.getItem(CACHE_KEY);
    const cachedTimestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (cachedData && cachedTimestamp) {
      const cacheAge = Date.now() - parseInt(cachedTimestamp, 10);
      if (cacheAge < CACHE_DURATION) {
        return {
          data: JSON.parse(cachedData),
          hasData: true,
        };
      }
    }
    return { data: null, hasData: false };
  } catch (e) {
    console.error("Error parsing cached PM Dashboard data:", e);
    return { data: null, hasData: false };
  }
};

const PlannedMaintenanceDashboard = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedPMDashboardData();
  const [data, setData] = useState(cachedInitialData.data);
  const [loading, setLoading] = useState(!cachedInitialData.hasData);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadData = async (showLoading = true) => {
      try {
        if (showLoading) {
          setLoading(true);
        }
        setError("");
        const tenantId = authService.getTenantId();
        if (!tenantId) {
          setError("Please login to view dashboard");
          setData(null);
          return;
        }
        const dashboard = await masterDataService.getPmDashboard(tenantId);
        setData(dashboard);

        // Fetch heatmap data optionally
        try {
          const heatmap = await pmMatrixService.getComplianceHeatmap(
            tenantId,
            new Date().getFullYear(),
          );
          dashboard.heatmapData = heatmap;
        } catch (hmErr) {
          console.log("Heatmap data could not be loaded", hmErr);
          // Fallback mock data if endpoint is missing to satisfy task.md requirement
          dashboard.heatmapData = {
            Jan: "🟩",
            Feb: "🟩",
            Mar: "🟥",
            Apr: "🟨",
            May: "🟩",
            Jun: "⬜",
          };
        }

        // Cache the data
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(dashboard));
        sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      } catch (err) {
        console.error("Error loading dashboard data:", err);
        setError("Failed to load dashboard data");
        // Don't clear cached data on error, keep showing it
        if (!cachedInitialData.hasData) {
          setData(null);
        }
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    };

    // Load fresh data in background if we have cached data, otherwise show loading
    const hasCachedData = cachedInitialData.hasData;
    loadData(!hasCachedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      Completed: "status-completed",
      Scheduled: "status-scheduled",
      Overdue: "status-overdue",
      Active: "status-active",
      "Pending Approval": "status-pending",
    };
    return statusMap[status] || "status-default";
  };

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      High: "priority-high",
      Medium: "priority-medium",
      Low: "priority-low",
    };
    return priorityMap[priority] || "priority-default";
  };

  if (loading) {
    return (
      <div className="pm-dashboard">
        <PageLoading message="Loading dashboard..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="pm-dashboard">
        <div className="error-message">
          {error || "Failed to load dashboard data"}
        </div>
      </div>
    );
  }

  const {
    summary,
    statusBreakdown,
    upcomingMaintenance,
    recentActivities,
    overdueMaintenance,
    plantWiseSummary,
  } = data;

  return (
    <div className="pagecontainer pm-dashboard">
      <div className="page-header">
        <div className="header-content">
          <h1>Machine Health Dashboard</h1>
          <p>Overview of maintenance plans, schedules, and activities</p>
        </div>
      </div>



      {/* Summary Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-icon">
              <HiOutlineCog />
            </span>
            <h3>Total Plans</h3>
          </div>
          <div className="stat-value">{summary.totalPlans}</div>
          <div className="stat-subtitle">{summary.activePlans} Active</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-icon">
              <HiOutlineCalendar />
            </span>
            <h3>Scheduled</h3>
          </div>
          <div className="stat-value">{summary.scheduledMaintenance}</div>
          <div className="stat-subtitle">This month</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-icon">
              <HiOutlineCheckCircle />
            </span>
            <h3>Completed</h3>
          </div>
          <div className="stat-value">{summary.completedThisMonth}</div>
          <div className="stat-subtitle">This month</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-icon">
              <HiOutlineExclamationTriangle />
            </span>
            <h3>Overdue</h3>
          </div>
          <div className="stat-value">{summary.overdue}</div>
          <div className="stat-subtitle">Requires attention</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-icon">
              <HiOutlineChartBar />
            </span>
            <h3>Compliance Rate</h3>
          </div>
          <div className="stat-value">{summary.complianceRate}%</div>
          <div className="stat-progress">
            <div
              className="stat-progress-bar"
              style={{ width: `${summary.complianceRate}%` }}
            ></div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-icon">
              <HiOutlineClock />
            </span>
            <h3>Avg. Completion</h3>
          </div>
          <div className="stat-value">{summary.averageCompletionTime}h</div>
          <div className="stat-subtitle">Average duration</div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="dashboard-section">
        <h3 className="section-title">Status Breakdown</h3>
        <div className="status-breakdown">
          {statusBreakdown.map((status, index) => (
            <div key={index} className="status-item">
              <div className="status-header">
                <span
                  className="status-dot"
                  style={{ backgroundColor: status.color }}
                ></span>
                <span className="status-label">{status.status}</span>
                <span className="status-count">{status.count}</span>
              </div>
              <div className="status-bar">
                <div
                  className="status-bar-fill"
                  style={{
                    width: `${status.percentage}%`,
                    backgroundColor: status.color,
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Maintenance Compliance Heatmap */}
      <div className="dashboard-section">
        <h3 className="section-title">PM Compliance Heatmap</h3>
        <div className="dashboard-card" style={{ padding: "1.5rem" }}>
          <p
            style={{
              color: "var(--text-secondary)",
              marginBottom: "1rem",
              fontSize: "14px",
            }}
          >
            Visual overview of maintenance compliance performance across the
            year.
          </p>
          <div
            className="heatmap-container"
            style={{
              display: "flex",
              gap: "8px",
              overflowX: "auto",
              paddingBottom: "8px",
            }}
          >
            {data.heatmapData &&
              Array.isArray(data.heatmapData) &&
              data.heatmapData.map((item) => {
                let emoji = "⬜";
                if (item.Overdue > 0) emoji = "🟥";
                else if (item.Completed > 0 && item.Pending === 0) emoji = "🟩";
                else if (item.Pending > 0 || item.TotalScheduled > 0)
                  emoji = "🟨";

                return (
                  <div
                    key={item.Month}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      minWidth: "40px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "var(--text-secondary)",
                      }}
                    >
                      {item.Month}
                    </span>
                    <span
                      style={{ fontSize: "24px", margin: "8px 0" }}
                      title={`Completed: ${item.Completed}, Overdue: ${item.Overdue}, Pending: ${item.Pending}`}
                    >
                      {emoji}
                    </span>
                  </div>
                );
              })}
            {data.heatmapData &&
              !Array.isArray(data.heatmapData) &&
              Object.entries(data.heatmapData).map(([month, emoji]) => (
                <div
                  key={month}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: "40px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {month}
                  </span>
                  <span style={{ fontSize: "24px", margin: "8px 0" }}>
                    {emoji}
                  </span>
                </div>
              ))}
          </div>
          <div
            style={{
              display: "flex",
              gap: "16px",
              fontSize: "12px",
              color: "var(--text-tertiary)",
              marginTop: "8px",
            }}
          >
            <span>🟩 Completed</span>
            <span>🟨 Scheduled</span>
            <span>🟥 Overdue</span>
            <span>⬜ No Data</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Upcoming Maintenance */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Upcoming Maintenance</h3>
            <span className="card-badge">{upcomingMaintenance.length}</span>
          </div>
          <div className="card-content">
            {upcomingMaintenance.length === 0 ? (
              <div className="empty-state">
                No upcoming maintenance scheduled
              </div>
            ) : (
              <div className="maintenance-list">
                {upcomingMaintenance.map((item) => (
                  <div key={item.id} className="maintenance-item">
                    <div className="maintenance-item-header">
                      <div className="maintenance-info">
                        <h4>{item.planName}</h4>
                        <p className="maintenance-meta">
                          {item.machineName} ({item.machineCode}) •{" "}
                          {item.plantName}
                        </p>
                      </div>
                      <span
                        className={`status-badge ${getStatusBadgeClass(item.status)}`}
                      >
                        {item.status}
                      </span>
                    </div>
                    <div className="maintenance-item-details">
                      <div className="detail-item">
                        <span className="detail-label">Scheduled:</span>
                        <span className="detail-value">
                          {formatDate(item.scheduledDate)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Duration:</span>
                        <span className="detail-value">
                          {item.estimatedDuration}h
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Technician:</span>
                        <span className="detail-value">
                          {item.assignedTechnician}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Priority:</span>
                        <span
                          className={`priority-badge ${getPriorityBadgeClass(item.priority)}`}
                        >
                          {item.priority}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Overdue Maintenance */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Overdue Maintenance</h3>
            <span className="card-badge error">
              {overdueMaintenance.length}
            </span>
          </div>
          <div className="card-content">
            {overdueMaintenance.length === 0 ? (
              <div className="empty-state success">No overdue maintenance</div>
            ) : (
              <div className="maintenance-list">
                {overdueMaintenance.map((item) => (
                  <div key={item.id} className="maintenance-item overdue">
                    <div className="maintenance-item-header">
                      <div className="maintenance-info">
                        <h4>{item.planName}</h4>
                        <p className="maintenance-meta">
                          {item.machineName} ({item.machineCode}) •{" "}
                          {item.plantName}
                        </p>
                      </div>
                      <span className="overdue-badge">
                        {item.daysOverdue} day{item.daysOverdue > 1 ? "s" : ""}{" "}
                        overdue
                      </span>
                    </div>
                    <div className="maintenance-item-details">
                      <div className="detail-item">
                        <span className="detail-label">Due Date:</span>
                        <span className="detail-value">
                          {formatDate(item.dueDate)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Technician:</span>
                        <span className="detail-value">
                          {item.assignedTechnician}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Reason:</span>
                        <span className="detail-value">{item.reason}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Priority:</span>
                        <span
                          className={`priority-badge ${getPriorityBadgeClass(item.priority)}`}
                        >
                          {item.priority}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="dashboard-section">
        <h3 className="section-title">Recent Activities</h3>
        <div className="dashboard-card">
          <div className="card-content">
            {recentActivities.length === 0 ? (
              <div className="empty-state">No recent activities</div>
            ) : (
              <div className="activities-table">
                <table>
                  <thead>
                    <tr>
                      <th>Plan Name</th>
                      <th>Machine</th>
                      <th>Plant</th>
                      <th>Completed Date</th>
                      <th>Completed By</th>
                      <th>Duration</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivities.map((activity) => (
                      <tr key={activity.id}>
                        <td>{activity.planName}</td>
                        <td>{activity.machineName}</td>
                        <td>{activity.plantName}</td>
                        <td>{formatDate(activity.completedDate)}</td>
                        <td>{activity.completedBy}</td>
                        <td>{activity.duration}h</td>
                        <td>
                          <span
                            className={`status-badge ${getStatusBadgeClass(activity.status)}`}
                          >
                            {activity.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Plant-wise Summary */}
      <div className="dashboard-section">
        <h3 className="section-title">Plant-wise Summary</h3>
        <div className="plant-summary-grid">
          {plantWiseSummary.map((plant) => (
            <div key={plant.plantId} className="plant-summary-card">
              <div className="plant-summary-header">
                <h4>{plant.plantName}</h4>
                <span className="plant-code">{plant.plantCode}</span>
              </div>
              <div className="plant-summary-stats">
                <div className="plant-stat">
                  <span className="plant-stat-label">Total Plans</span>
                  <span className="plant-stat-value">{plant.totalPlans}</span>
                </div>
                <div className="plant-stat">
                  <span className="plant-stat-label">Active</span>
                  <span className="plant-stat-value">{plant.activePlans}</span>
                </div>
                <div className="plant-stat">
                  <span className="plant-stat-label">Completed</span>
                  <span className="plant-stat-value">
                    {plant.completedThisMonth}
                  </span>
                </div>
                <div className="plant-stat">
                  <span className="plant-stat-label">Overdue</span>
                  <span className="plant-stat-value error">
                    {plant.overdue}
                  </span>
                </div>
              </div>
              <div className="plant-compliance">
                <div className="compliance-header">
                  <span>Compliance Rate</span>
                  <span className="compliance-value">
                    {plant.complianceRate}%
                  </span>
                </div>
                <div className="compliance-bar">
                  <div
                    className="compliance-bar-fill"
                    style={{ width: `${plant.complianceRate}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlannedMaintenanceDashboard;
